import { graphql, Link, useStaticQuery } from "gatsby";
import React from "react";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { CTAButton } from "./CTAButton";

type CompanyProps = {
  size?: "default" | "large";
  className?: string;
};

type Navigation = {
  label: string;
  url: string;
  type: "primary" | "outline" | "link";
  opensInNewTab: boolean;
};

type CTAQueryData = {
  draftboxSettings: {
    content: {
      cta: {
        title: string;
        subTitle: string;
        navigation: Navigation[];
        textStyle: "light" | "dark";
        readabilityOverlay: boolean;
        backgroundColor: string;
        contentAlignment: "left" | "center";
        visible: boolean;
        hideBackgroundImageOnMobile: boolean;
        coverImage: {
          publicURL: string;
          childImageSharp: {
            gatsbyImageData: any;
          };
        };
      };
    };
  };
};

const Company: React.FC<CompanyProps> = ({ size = "default", className }) => {
  const {
    draftboxSettings: {
      content: { cta },
    },
  } = useStaticQuery<CTAQueryData>(graphql`
    {
      draftboxSettings {
        content {
          cta {
            hideBackgroundImageOnMobile
            title
            subTitle
            coverImage {
              publicURL
              childImageSharp {
                gatsbyImageData(
                  quality: 100
                  placeholder: TRACED_SVG
                  layout: FULL_WIDTH
                )
              }
            }
            navigation {
              label
              url
              type
              opensInNewTab
            }
            textStyle
            readabilityOverlay
            backgroundColor
            contentAlignment
          }
        }
      }
    }
  `);

  return (
    <div className={className}>
      <div
        className={classNames(
          "overflow-hidden flex flex-col relative justify-center px-6 md:px-12",
          {
            "py-8 md:py-16": size === "large",
            "py-6 md:py-10": size === "default",
            "items-center text-center": cta.contentAlignment === "center",
            "items-start": cta.contentAlignment === "left",
          }
        )}
      >
        {cta?.coverImage && (
          <>
            <div
              style={{ zIndex: -2 }}
              className={classNames(
                "absolute inset-0 h-full w-full rounded-lg object-cover",
                {
                  "hidden md:block": cta.hideBackgroundImageOnMobile,
                }
              )}
            >
              {cta?.coverImage?.childImageSharp && (
                <GatsbyImage
                  alt="company cover image"
                  image={cta.coverImage.childImageSharp.gatsbyImageData}
                  className="h-full rounded-lg"
                />
              )}
              {!cta?.coverImage?.childImageSharp?.gatsbyImageData &&
                cta?.coverImage?.publicURL && (
                  <img
                    className="h-full w-full rounded-lg"
                    src={cta.coverImage.publicURL}
                  />
                )}
            </div>
            {cta.coverImage?.publicURL && (
              <div
                style={{ zIndex: -1 }}
                className={classNames(
                  "h-full rounded-lg w-full absolute inset-0",
                  {
                    "bg-white bg-opacity-75":
                      cta.readabilityOverlay && cta.textStyle === "dark",
                    "bg-black bg-opacity-75":
                      cta.readabilityOverlay && cta.textStyle === "light",
                  }
                )}
              ></div>
            )}
          </>
        )}
        <div
          style={{ zIndex: -3, background: cta.backgroundColor }}
          className={"h-full inset-0 w-full absolute rounded-lg block"}
        ></div>
        <h1
          className={classNames(
            "text-2xl lg:text-3xl mt-0 mb-2 leading-tight font-sansSemibold",
            {
              "text-gray-800": cta.textStyle === "dark",
              "text-white": cta.textStyle === "light",
            }
          )}
        >
          {cta.title}
        </h1>
        <p
          className={classNames(
            "mt-2 text-base lg:text-xl font-serifNormal max-w-2xl",
            {
              "text-gray-800": cta.textStyle === "dark",
              "text-white": cta.textStyle === "light",
            }
          )}
        >
          {cta.subTitle}
        </p>

        <div
          className={classNames("flex flex-wrap items-center", {
            "justify-center": cta?.contentAlignment === "center",
          })}
        >
          {cta.navigation.map((button, index) => {
            return (
              <div
                key={index}
                className={classNames(
                  "mt-5",
                  {
                    "mr-4":
                      cta?.contentAlignment === "left" &&
                      cta?.navigation?.length > 1 &&
                      index !== cta?.navigation?.length - 1,
                  },
                  {
                    "mx-2":
                      cta?.contentAlignment === "center" &&
                      cta?.navigation?.length > 1,
                  }
                )}
              >
                <CTAButton
                  type={button.type}
                  label={button.label}
                  opensInNewTab={button.opensInNewTab}
                  url={button.url}
                  textStyle={cta.textStyle}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Company;
